import { type MouseEvent, type ReactNode, useRef } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
// @ts-expect-error pure js
import DraggableItem from "./DraggableItem";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./SortablePhotoGrid.module.css";

interface SortablePhotoGridProps<T> {
  items: T[];
  getId: (item: T) => string | number;
  onReorder: (items: T[]) => void;
  children: (item: T, index: number) => ReactNode;
  className?: string;
}

export default function SortablePhotoGrid<T>({
  items,
  getId,
  onReorder,
  children,
  className,
}: SortablePhotoGridProps<T>) {
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  // Un drag se termine par un `click` synthétique : on le neutralise pour qu'il
  // ne déclenche pas l'action au clic d'une tuile (ex. ouverture de l'aperçu).
  const draggingRef = useRef(false);

  function resetDraggingSoon() {
    requestAnimationFrame(() => {
      draggingRef.current = false;
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    resetDraggingSoon();
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => getId(item) === active.id);
    const newIndex = items.findIndex((item) => getId(item) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  function suppressClickAfterDrag(event: MouseEvent) {
    if (draggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => {
        draggingRef.current = true;
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={resetDraggingSoon}
    >
      <SortableContext items={items.map(getId)} strategy={rectSortingStrategy}>
        <div
          className={classnames(styles.grid, className)}
          onClickCapture={suppressClickAfterDrag}
        >
          {items.map((item, index) => (
            <DraggableItem key={getId(item)} id={getId(item)}>
              {children(item, index)}
            </DraggableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
